import { use } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ModeEditOutlineOutlined from '@mui/icons-material/ModeEditOutlineOutlined';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PopUp from 'shared/components/pop-up/pop-up';

import CareConnectSettingsProviderContext from 'features/care-connect-settings/contexts/care-connect-settings-provider-context';
import CallPlanExternalNumbers from 'features/care-connect-settings/views/components/call-plan-external-numbers/call-plan-external-numbers';
import CallPlanInternalNumbers from 'features/care-connect-settings/views/components/call-plan-internal-numbers/call-plan-internal-numbers';
import CallPlanLabel from 'features/care-connect-settings/views/components/call-plan-label/call-plan-label';
import CallPlanRingingStrategy from 'features/care-connect-settings/views/components/call-plan-ringing-strategy/call-plan-ringing-strategy';
import CallPlanToggleRow from 'features/care-connect-settings/views/components/call-plan-toggle-row/call-plan-toggle-row';
import styles from 'features/care-connect-settings/views/components/care-connect-call-plan-details.module.scss';

const CareConnectCallPlanDetails = (): React.JSX.Element => {
  const { t } = useTranslation('careConnectSettings');

  const { selectedOrganisationUnit, selectedOrganisationUnitBreadcrumbs, callPlanDetails } = use(CareConnectSettingsProviderContext);

  const {
    callPlanForm,
    isEditing,
    callPlanIsLoading,
    handleCallPlanEdit,
    handleCallPlanCancel,
    submitCallPlan,
    isUpsertCallPlanErrorPopupOpen,
    closeUpsertCallPlanErrorPopup,
  } = callPlanDetails;
  const isCallPlanEnabled = useWatch({ control: callPlanForm.control, name: 'isCallPlanEnabled' });

  const renderViewingModeButtons = (): React.JSX.Element => (
    <Button startIcon={<ModeEditOutlineOutlined />} variant="text" onClick={handleCallPlanEdit} disabled={callPlanIsLoading}>
      {t('details.editButton')}
    </Button>
  );

  const renderEditingModeButtons = (): React.JSX.Element => (
    <>
      <Button variant="text" onClick={handleCallPlanCancel} disabled={callPlanIsLoading}>
        {t('details.cancelButton')}
      </Button>
      <Button startIcon={<CheckRounded />} variant="contained" type="submit" disabled={callPlanIsLoading}>
        {t('details.saveButton')}
      </Button>
    </>
  );

  return (
    <div className={styles.container}>
      <form onSubmit={callPlanForm.handleSubmit(submitCallPlan)} className={styles.form}>
        <div className={styles.header}>
          <div className={styles.actions}>{isEditing ? renderEditingModeButtons() : renderViewingModeButtons()}</div>
          <div className={styles.title}>
            <Typography variant="h3">{selectedOrganisationUnit?.name}</Typography>
            {selectedOrganisationUnitBreadcrumbs.length > 0 && (
              <BreadCrumb
                showEllipsis={selectedOrganisationUnitBreadcrumbs.length > 4}
                allElementNames={selectedOrganisationUnitBreadcrumbs}
              >
                {selectedOrganisationUnitBreadcrumbs.map((name) => (
                  <Typography key={`breadcrumb-${name}`} variant="body1">
                    {name}
                  </Typography>
                ))}
              </BreadCrumb>
            )}
          </div>
        </div>
        <div className={styles.content}>
          {callPlanIsLoading ? (
            <LoadingIndicator />
          ) : (
            <>
              <CallPlanToggleRow />
              <Collapse in={isCallPlanEnabled} unmountOnExit>
                <div className={styles.callPlanSettings}>
                  <CallPlanLabel />
                  <CallPlanExternalNumbers />
                  <CallPlanInternalNumbers />
                  <CallPlanRingingStrategy />
                </div>
              </Collapse>
            </>
          )}
        </div>
      </form>
      <PopUp
        isOpen={isUpsertCallPlanErrorPopupOpen}
        title={t('upsertCallPlanErrorPopup.title')}
        body={t('upsertCallPlanErrorPopup.body')}
        primaryButtonText={t('upsertCallPlanErrorPopup.confirm')}
        primaryButtonAction={closeUpsertCallPlanErrorPopup}
        handleOnClose={closeUpsertCallPlanErrorPopup}
      />
    </div>
  );
};

export default CareConnectCallPlanDetails;
